import { useState, useEffect, useCallback } from 'react';
import _debounce from 'lodash/debounce';

interface OptimizedScrollOptions {
  /** Initial scroll threshold in pixels before the bottom where loading starts */
  initialThreshold?: number;
  /** Maximum threshold adjustment based on network speed */
  maxThresholdAdjustment?: number;
  /** Enables dynamic threshold adjustment based on performance */
  enableDynamicThreshold?: boolean;
  /** Callback function when scroll threshold is reached */
  onThresholdReached?: () => void;
  /** Debounce delay in milliseconds */
  debounceDelay?: number;
}

/**
 * A hook that provides optimized infinite scroll functionality
 * It dynamically adjusts the threshold based on network conditions and scroll speed
 * Uses Lodash's debounce for performance optimization
 */
const useOptimizedScroll = ({
  initialThreshold = 600,
  maxThresholdAdjustment = 1000,
  enableDynamicThreshold = true,
  onThresholdReached,
  debounceDelay = 300, // Default debounce delay similar to AutocompleteMain
}: OptimizedScrollOptions = {}) => {
  const [isNearBottom, setIsNearBottom] = useState(false);
  const [threshold, setThreshold] = useState(initialThreshold);
  const [lastLoadTime, setLastLoadTime] = useState<number | null>(null);
  const [networkSpeed, setNetworkSpeed] = useState<'fast' | 'medium' | 'slow'>('medium');

  // Detect network conditions using Navigation Timing API
  useEffect(() => {
    if (typeof window !== 'undefined' && window.performance && window.performance.getEntriesByType) {
      const resources = window.performance.getEntriesByType('resource') as PerformanceResourceTiming[];

      if (resources.length) {
        // Calculate average duration of the last 5 resource loads
        const recentResources = resources.slice(-5);
        const avgDuration =
          recentResources.reduce((sum, resource) => sum + resource.duration, 0) / recentResources.length;

        // Classify network speed
        if (avgDuration < 200) {
          setNetworkSpeed('fast');
        } else if (avgDuration < 1000) {
          setNetworkSpeed('medium');
        } else {
          setNetworkSpeed('slow');
        }
      }
    }
  }, [lastLoadTime]);

  // Adjust threshold based on network speed
  useEffect(() => {
    if (!enableDynamicThreshold) return;

    let newThreshold = initialThreshold;

    if (networkSpeed === 'fast') {
      // Smaller threshold for fast connections (preload more aggressively)
      newThreshold = initialThreshold + maxThresholdAdjustment * 0.5;
    } else if (networkSpeed === 'medium') {
      // Default threshold
      newThreshold = initialThreshold;
    } else {
      // Larger threshold for slow connections (start loading earlier)
      newThreshold = initialThreshold + maxThresholdAdjustment;
    }

    setThreshold(newThreshold);
  }, [networkSpeed, initialThreshold, maxThresholdAdjustment, enableDynamicThreshold]);

  // Check if user is near bottom when scrolling - debounced for better performance
  const handleScroll = useCallback(
    _debounce(() => {
      if (!onThresholdReached) return;

      const scrollPosition = window.scrollY + window.innerHeight;
      const scrollHeight = document.documentElement.scrollHeight;

      // Check if user is within the threshold of the bottom
      const isWithinThreshold = scrollHeight - scrollPosition < threshold;

      if (isWithinThreshold && !isNearBottom) {
        setIsNearBottom(true);
        onThresholdReached();
        setLastLoadTime(Date.now());
      } else if (!isWithinThreshold && isNearBottom) {
        setIsNearBottom(false);
      }
    }, debounceDelay), // Use the debounceDelay from props
    [threshold, isNearBottom, onThresholdReached, debounceDelay]
  );

  // Add scroll event listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return {
    isNearBottom,
    threshold,
    networkSpeed,
  };
};

export default useOptimizedScroll;
